// Histórico de remuneração por vigência, dentro da ficha do profissional.
//
// Pedido do Lucas (21/08): "precisamos ter o histórico de remuneração deles".
//
// ⚠️ O que este bloco NÃO é: a fonte do custo. O custo que entra no lucro por
// contrato sai da nota de pagamento de cada mês, então aumento já entrava
// sozinho. O que estava quebrado era o coeficiente e a carga horária serem
// valor ÚNICO: como o fechamento reprocessa os últimos meses, baixar hoje a
// carga de alguém reescrevia o R$/h de meses já fechados. Registrar vigência
// é o que impede o passado de mudar quando o presente muda.
//
// Lápis de corrigir (05/10/2026, pedido do Lucas): conserta um NÚMERO cadastrado
// errado na própria vigência (a de agosto nasceu com 8 h/dia para todo mundo; a
// Lindsay, de 6 h, apareceu "devendo" no painel). Antes era apagar e redigitar o
// salário. Data não se corrige por aqui — isso é "Nova vigência". O servidor
// deixa o rastro no motivo (quem, quando, o quê).
import { useCallback, useEffect, useState } from 'react';
import {
  App as AntApp, Button, Col, DatePicker, Form, InputNumber, Input, Modal, Popconfirm,
  Row, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

const { Text } = Typography;

type Vigencia = {
  id: string;
  vigencia_inicio: string;
  vigencia_fim: string | null;
  salario: string | null;
  coeficiente: string;
  horas_dia: string;
  motivo: string | null;
  criado_por: string | null;
};

const brl = (v: string | null) =>
  v === null || v === undefined ? '—'
    : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function HistoricoRemuneracao({ profissionalId, aoMudar }: {
  profissionalId: string;
  aoMudar?: () => void;
}) {
  const { message } = AntApp.useApp();
  const [linhas, setLinhas] = useState<Vigencia[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form] = Form.useForm();
  // A vigência que o lápis está corrigindo (null = modal fechado).
  const [corrigindo, setCorrigindo] = useState<Vigencia | null>(null);
  const [formCorrecao] = Form.useForm();

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const token = await getToken();
      const r = await axios.get(
        `${API_URL}/financeiro/remuneracao?profissional_id=${profissionalId}`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setLinhas(d.vigencias || []);
    } catch (e: any) {
      // Sem permissão o bloco simplesmente não lista — o aviso já é dado acima.
      setLinhas([]);
    } finally { setCarregando(false); }
  }, [profissionalId]);

  useEffect(() => { carregar(); }, [carregar]);

  async function salvar() {
    let v: any;
    try { v = await form.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/financeiro/remuneracao`, {
        profissional_id: profissionalId,
        vigencia_inicio: (v.vigencia_inicio as Dayjs).format('YYYY-MM-DD'),
        salario: v.salario ?? null,
        coeficiente: v.coeficiente,
        horas_dia: v.horas_dia,
        motivo: v.motivo || null,
      }, { headers: { Authorization: token } });
      message.success('Vigência registrada');
      setAberto(false); form.resetFields();
      await carregar();
      aoMudar?.();
    } catch (e: any) {
      const d = e.response?.data;
      message.error(d?.erro || d?.error || e.message);
    } finally { setSalvando(false); }
  }

  function abrirCorrecao(r: Vigencia) {
    formCorrecao.setFieldsValue({
      salario: r.salario === null || r.salario === undefined ? null : Number(r.salario),
      coeficiente: Number(r.coeficiente),
      horas_dia: Number(r.horas_dia),
      observacao: '',
    });
    setCorrigindo(r);
  }

  async function corrigir() {
    if (!corrigindo) return;
    let v: any;
    try { v = await formCorrecao.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.patch(`${API_URL}/financeiro/remuneracao/${corrigindo.id}`, {
        salario: v.salario ?? null,
        coeficiente: v.coeficiente,
        horas_dia: v.horas_dia,
        observacao: v.observacao || null,
      }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if ((d.mudou || []).length) message.success('Vigência corrigida');
      else message.info('Nada mudou — os valores já eram esses');
      setCorrigindo(null);
      await carregar();
      aoMudar?.();
    } catch (e: any) {
      const d = e.response?.data;
      message.error(d?.erro || d?.error || e.message);
    } finally { setSalvando(false); }
  }

  async function excluir(id: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/financeiro/remuneracao/${id}`, { headers: { Authorization: token } });
      await carregar();
      aoMudar?.();
    } catch (e: any) {
      const d = e.response?.data;
      message.error(d?.erro || d?.error || e.message);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          📈 Histórico de remuneração
        </Text>
        <Button size="small" icon={<PlusOutlined />} onClick={() => {
          form.setFieldsValue({
            // Primeiro dia do mês que vem: mudança no meio do mês re-precifica
            // um mês que já corria com outro número.
            vigencia_inicio: dayjs().add(1, 'month').startOf('month'),
            coeficiente: Number(linhas[0]?.coeficiente ?? 1),
            horas_dia: Number(linhas[0]?.horas_dia ?? 8),
            salario: linhas[0]?.salario ? Number(linhas[0].salario) : null,
          });
          setAberto(true);
        }}>
          Nova vigência
        </Button>
      </div>
      <div style={{ fontSize: 11, color: '#888', margin: '4px 0 8px' }}>
        Registrar uma vigência nova <strong>fecha a anterior</strong> no dia anterior. O
        fechamento de cada mês usa o que valia naquele mês — por isso mexer aqui não reescreve
        o passado. Número cadastrado errado? Use o lápis da linha.
      </div>

      <Table
        rowKey="id" size="small" dataSource={linhas} loading={carregando} pagination={false}
        locale={{ emptyText: 'Sem vigência registrada — o cadastro atual vale para tudo.' }}
        columns={[
          {
            title: 'Período', width: 190,
            render: (_: any, r: Vigencia) => (
              <span>
                {dayjs(r.vigencia_inicio).format('DD/MM/YYYY')}
                {' → '}
                {r.vigencia_fim
                  ? dayjs(r.vigencia_fim).format('DD/MM/YYYY')
                  : <Tag color="green" style={{ marginInlineEnd: 0 }}>vigente</Tag>}
              </span>
            ),
          },
          { title: 'Salário', dataIndex: 'salario', width: 130, align: 'right',
            render: (v: string | null) => brl(v) },
          { title: 'Coef.', dataIndex: 'coeficiente', width: 80, align: 'right',
            render: (v: string) => Number(v).toFixed(2) },
          { title: 'Horas/dia', dataIndex: 'horas_dia', width: 90, align: 'right',
            render: (v: string) => Number(v).toFixed(v.endsWith('.00') ? 0 : 2) },
          { title: 'Motivo', dataIndex: 'motivo',
            render: (m: string | null) => m || <Text type="secondary">—</Text> },
          {
            title: '', width: 84,
            render: (_: any, r: Vigencia) => (
              <Space size={0}>
                <Tooltip title="Corrigir um número cadastrado errado">
                  <Button size="small" type="text" icon={<EditOutlined />} aria-label="Corrigir vigência"
                    onClick={() => abrirCorrecao(r)} />
                </Tooltip>
                <Popconfirm
                  title="Apagar esta vigência?"
                  description="O período anterior NÃO reabre sozinho — confira as datas depois."
                  okText="Apagar" okButtonProps={{ danger: true }} cancelText="Não"
                  onConfirm={() => excluir(r.id)}
                >
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Apagar vigência" />
                </Popconfirm>
              </Space>
            ),
          },
        ] as any}
      />

      <Modal
        open={!!corrigindo} title="Corrigir vigência"
        onCancel={() => setCorrigindo(null)} onOk={corrigir} confirmLoading={salvando}
        okText="Corrigir" cancelText="Cancelar" width="94vw" style={{ maxWidth: 640, top: 24 }}
      >
        {corrigindo && (
          <div style={{ fontSize: 12, color: '#666', margin: '4px 0 12px' }}>
            <div>
              <Text strong>Período:</Text>{' '}
              {dayjs(corrigindo.vigencia_inicio).format('DD/MM/YYYY')} → {corrigindo.vigencia_fim
                ? dayjs(corrigindo.vigencia_fim).format('DD/MM/YYYY') : 'vigente'}
              {' '}<Text type="secondary" style={{ fontSize: 12 }}>(a data não muda aqui)</Text>
            </div>
            <div style={{ marginTop: 6 }}>
              Use para consertar um número que foi cadastrado errado. Se o valor mudou de verdade a
              partir de uma data (aumento, passou a meio período), registre uma <strong>Nova vigência</strong>.
              Corrigir horas por dia ou coeficiente muda o custo por hora dos meses que o sistema
              ainda recalcula (os 3 últimos).
            </div>
          </div>
        )}
        <Form form={formCorrecao} layout="vertical">
          {/* Os três números lado a lado: empilhados, a janela passava da tela de notebook. */}
          <Row gutter={12}>
            <Col xs={24} sm={8}>
              <Form.Item name="salario" label="Salário (R$)">
                <InputNumber min={0} precision={2} decimalSeparator="," style={{ width: '100%' }}
                  placeholder="sem salário registrado" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="coeficiente" label="Coeficiente" tooltip="Encargos, 13º e férias sobre o salário"
                rules={[{ required: true, message: 'informe o coeficiente' }]}>
                <InputNumber min={1} max={5} step={0.05} precision={4} decimalSeparator="," style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="horas_dia" label="Horas por dia"
                rules={[{ required: true, message: 'informe a carga' }]}>
                <InputNumber min={1} max={24} step={1} precision={2} decimalSeparator="," style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="observacao" label="O que estava errado (opcional)"
            extra="Quem corrigiu, quando e o que mudou ficam registrados no motivo da vigência.">
            <Input maxLength={200} placeholder="ex.: estagiária, sempre foram 6 h" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={aberto} title="Nova vigência de remuneração"
        onCancel={() => setAberto(false)} onOk={salvar} confirmLoading={salvando}
        okText="Registrar" width="94vw" style={{ maxWidth: 520, top: 24 }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="vigencia_inicio" label="Vale a partir de"
            rules={[{ required: true, message: 'informe a data' }]}>
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} allowClear={false} />
          </Form.Item>
          <Form.Item name="salario" label="Salário (R$)">
            <InputNumber min={0} precision={2} style={{ width: '100%' }}
              formatter={(v) => (v === undefined || v === null ? '' : String(v).replace('.', ','))}
              parser={(v) => (v ? Number(String(v).replace(',', '.')) : 0) as any} />
          </Form.Item>
          <Form.Item name="coeficiente" label="Coeficiente (encargos, 13º, férias)"
            rules={[{ required: true, message: 'informe o coeficiente' }]}>
            <InputNumber min={1} max={5} step={0.05} precision={4} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="horas_dia" label="Horas por dia"
            rules={[{ required: true, message: 'informe a carga' }]}>
            <InputNumber min={1} max={24} step={1} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="motivo" label="Motivo (opcional)">
            <Input placeholder="ex.: passou a meio período" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
